import React, { useState, useMemo } from "react"
import { useTranslation } from "react-i18next"
import type { TFunction } from "i18next"
import {
  useReactTable,
  getCoreRowModel,
  type ColumnDef,
  flexRender,
} from "@tanstack/react-table"
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core"
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Checkbox } from "@/components/ui/checkbox"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "@/components/ui/context-menu"
import { Badge } from "@/components/ui/badge"
import { MoreHorizontal, GripVertical, AlertTriangle } from "lucide-react"
import type { ReportListItem } from "@/types/report-list"
import type { ContextMenuCommand, ContextMenuItem as ContextMenuItemType } from "@/lib/context-menu-items"
import { ParameterSummaryBadges } from "./parameter-summary-badges"
import { getReportListItemMenuItems, getSelectedReportListItemMenuItems } from "@/lib/context-menu-items"
import { hasUnavailableParameterValues } from "@/lib/report-list-helpers"

interface ReportListGridProps {
  items: ReportListItem[]
  isParameterSummaryLoading?: boolean
  selectedItems: ReportListItem[]
  onSelectionChange: (items: ReportListItem[]) => void
  activeItem: ReportListItem | null
  onActiveItemChange: (item: ReportListItem | null) => void
  onItemUpdate: (item: ReportListItem) => void
  onItemsReorder: (items: ReportListItem[]) => void
  onContextAction: (command: ContextMenuCommand, item: ReportListItem) => void
}

interface SelectAllHeaderProps {
  selectableItemCount: number
  selectedSelectableCount: number
  onSelectAll: (checked: boolean) => void
}

function SelectAllHeader({
  selectableItemCount,
  selectedSelectableCount,
  onSelectAll,
}: Readonly<SelectAllHeaderProps>) {
  return (
    <Checkbox
      checked={
        selectableItemCount > 0 &&
        selectedSelectableCount === selectableItemCount
      }
      onCheckedChange={(checked) => onSelectAll(checked as boolean)}
    />
  )
}

interface SelectCellProps {
  item: ReportListItem
  selected: boolean
  disabled: boolean
  onSelectItem: (item: ReportListItem, checked: boolean) => void
}

function SelectCell({ item, selected, disabled, onSelectItem }: Readonly<SelectCellProps>) {
  return (
    <Checkbox
      checked={selected}
      onCheckedChange={(checked) => onSelectItem(item, checked as boolean)}
      disabled={disabled}
    />
  )
}

interface DisplayNameCellProps {
  item: ReportListItem
  isEditing: boolean
  editingValue: string
  onEditValueChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onEditBlur: () => void
  onEditKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void
  onDoubleClick: () => void
  t: (key: string) => string
}

function DisplayNameCell({
  item,
  isEditing,
  editingValue,
  onEditValueChange,
  onEditBlur,
  onEditKeyDown,
  onDoubleClick,
  t,
}: Readonly<DisplayNameCellProps>) {
  const isInactive = !!item.report?.inactiveDateTime
  const needsAttention = hasUnavailableParameterValues(item)

  if (isEditing) {
    return (
      <Input
        value={editingValue}
        onChange={onEditValueChange}
        onBlur={onEditBlur}
        onKeyDown={onEditKeyDown}
        autoFocus
        className="h-8"
      />
    )
  }

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        data-activates-row
        onDoubleClick={onDoubleClick}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            onDoubleClick()
          }
        }}
        className={`cursor-text bg-transparent border-none p-0 text-left text-inherit font-inherit text-sm ${
          isInactive ? "text-muted-foreground" : ""
        }`}
      >
        {item.displayName || item.report?.name || t("grid.unnamedReport")}
      </button>
      {isInactive && (
        <Badge variant="outline" className="text-xs text-muted-foreground">
          {t("grid.inactive")}
        </Badge>
      )}
      {needsAttention && (
        <Badge
          variant="outline"
          title={t("grid.needsAttentionHint")}
          className="shrink-0 gap-1 border-amber-500 text-xs text-amber-700 dark:text-amber-400"
        >
          <AlertTriangle aria-hidden="true" className="h-3 w-3" />
          {t("grid.needsAttention")}
        </Badge>
      )}
    </div>
  )
}

interface ActionCellProps {
  item: ReportListItem
  onMenuItemClick: (command: ContextMenuCommand, item: ReportListItem) => void
  menuItems: ContextMenuItemType[]
  selectedCount: number
  t: TFunction
}

function ActionCell({ item, onMenuItemClick, menuItems, selectedCount, t }: Readonly<ActionCellProps>) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {selectedCount > 0 && (
          <>
            <DropdownMenuLabel className="bg-muted rounded-sm text-foreground text-sm font-normal">
              {t("grid.selectedCount", { count: selectedCount })}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
          </>
        )}
        {menuItems.map((menuItem) =>
          menuItem.separator ? (
            <DropdownMenuSeparator key={`sep-${menuItem.command}`} />
          ) : (
            <DropdownMenuItem
              key={menuItem.command}
              className={menuItem.destructive ? "text-destructive" : ""}
              onClick={() => onMenuItemClick(menuItem.command, item)}
            >
              {menuItem.label}
            </DropdownMenuItem>
          ),
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

interface SortableRowProps extends React.ComponentPropsWithoutRef<typeof TableRow> {
  item: ReportListItem
  isActive: boolean
  onRowClick: (item: ReportListItem) => void
  onContextMenuOpen: (item: ReportListItem) => void
  children: React.ReactNode
}

function SortableRow({
  item,
  isActive,
  onRowClick,
  onContextMenuOpen,
  children,
  onContextMenu,
  ...rowProps
}: Readonly<SortableRowProps>) {
  const isDisabled = !!item.report?.isHidden
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.reportListItemKey, disabled: isDisabled })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    backgroundColor: isDragging ? "var(--accent)" : undefined,
  }

  const handleClick = (e: React.MouseEvent) => {
    // Don't toggle selection when clicking interactive elements
    const target = e.target as HTMLElement
    if (
      target.closest('[role="checkbox"]') ||
      target.closest("input") ||
      target.closest('[data-drag-handle="true"]') ||
      (target.closest("button") && !target.closest("[data-activates-row]"))
    ) {
      return
    }
    onRowClick(item)
  }

  const handleContextMenu = (e: React.MouseEvent<HTMLTableRowElement>) => {
    onContextMenuOpen(item)
    onContextMenu?.(e)
  }

  return (
    <TableRow
      {...rowProps}
      ref={setNodeRef}
      style={style}
      className={`${isDisabled ? "opacity-50 pointer-events-none" : "cursor-pointer"} ${isActive ? "bg-accent" : ""}`}
      onClick={handleClick}
      onContextMenu={handleContextMenu}
    >
      <TableCell className="w-8">
        {!isDisabled && (
          <div data-drag-handle="true" {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing">
            <GripVertical className="h-4 w-4 text-muted-foreground" />
          </div>
        )}
      </TableCell>
      {children}
    </TableRow>
  )
}

export function ReportListGrid({
  items,
  isParameterSummaryLoading = false,
  selectedItems,
  onSelectionChange,
  activeItem,
  onActiveItemChange,
  onItemUpdate,
  onItemsReorder,
  onContextAction,
}: Readonly <ReportListGridProps>)
{
  const { t } = useTranslation();
  const [editingItemKey, setEditingItemKey] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState("");
  const reportListItemMenuItems = useMemo(() => getReportListItemMenuItems(t), [t]);
  const selectedReportListItemMenuItems = useMemo(
    () => getSelectedReportListItemMenuItems(t),
    [t],
  );
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex((item) => item.reportListItemKey === active.id)
      const newIndex = items.findIndex((item) => item.reportListItemKey === over.id)

      const newItems = [...items]
      const [movedItem] = newItems.splice(oldIndex, 1)
      newItems.splice(newIndex, 0, movedItem)

      // Update display order for all affected items
      const updatedItems = newItems.map((item, index) => ({
        ...item,
        displayOrder: index,
      }))

      onItemsReorder(updatedItems)
    }
  }

  const handleStartEdit = React.useCallback((item: ReportListItem) => {
    setEditingItemKey(item.reportListItemKey)
    setEditingValue(item.displayName || "")
  }, [])

  const handleEditValueChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingValue(e.target.value)
  }, [])

  const handleSaveEdit = React.useCallback(
    (item: ReportListItem) => {
      if (editingValue.trim() && editingValue !== item.displayName) {
        onItemUpdate({
          ...item,
          displayName: editingValue.trim(),
        })
      }
      setEditingItemKey(null)
      setEditingValue("")
    },
    [editingValue, onItemUpdate],
  )

  const handleCancelEdit = React.useCallback(() => {
    setEditingItemKey(null)
    setEditingValue("")
  }, [])

  const handleEditKeyDown = React.useCallback(
    (item: ReportListItem) => (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        handleSaveEdit(item)
      } else if (e.key === "Escape") {
        handleCancelEdit()
      }
    },
    [handleSaveEdit, handleCancelEdit],
  )

  const handleEditBlur = React.useCallback(
    (item: ReportListItem) => () => {
      handleSaveEdit(item)
    },
    [handleSaveEdit],
  )

  const handleDoubleClick = React.useCallback(
    (item: ReportListItem) => () => {
      handleStartEdit(item)
    },
    [handleStartEdit],
  )

  const handleContextMenuAction = React.useCallback(
    (command: ContextMenuCommand, item: ReportListItem) => {
      if (command === "renameItem") {
        handleStartEdit(item)
      } else {
        onContextAction(command, item)
      }
    },
    [onContextAction, handleStartEdit],
  )

  const handleMenuItemClick = React.useCallback(
    (command: ContextMenuCommand, item: ReportListItem) => () => {
      handleContextMenuAction(command, item)
    },
    [handleContextMenuAction],
  )

  const isSelected = React.useCallback(
    (item: ReportListItem) =>
      selectedItems.some((si) => si.reportListItemKey === item.reportListItemKey),
    [selectedItems],
  )

  const isItemSelectionDisabled = React.useCallback(
    (item: ReportListItem) => !!item.report?.isHidden || !!item.report?.inactiveDateTime,
    [],
  )

  const selectableItems = useMemo(
    () => items.filter((item) => !isItemSelectionDisabled(item)),
    [items, isItemSelectionDisabled],
  )

  const selectedSelectableCount = useMemo(
    () => selectedItems.filter((item) => !isItemSelectionDisabled(item)).length,
    [isItemSelectionDisabled, selectedItems],
  )

  const handleSelectAll = React.useCallback(
    (checked: boolean) => {
      onSelectionChange(checked ? selectableItems : [])
    },
    [onSelectionChange, selectableItems],
  )

  const handleSelectItem = React.useCallback(
    (item: ReportListItem, checked: boolean) => {
      if (isItemSelectionDisabled(item)) {
        return
      }

      if (checked) {
        onSelectionChange([...selectedItems, item])
      } else {
        onSelectionChange(selectedItems.filter((si) => si.reportListItemKey !== item.reportListItemKey))
      }
    },
    [isItemSelectionDisabled, onSelectionChange, selectedItems],
  )

  const handleRowClickActivate = (item: ReportListItem) => {
    onActiveItemChange(item)
  }

  // Skip single-item activation when the row's checkbox is checked; the menu then applies to all checked rows
  const handleRowContextMenuOpen = (item: ReportListItem) => {
    if (!isSelected(item)) {
      onActiveItemChange(item)
    }
  }

  const isActiveItem = React.useCallback(
    (item: ReportListItem) =>
      activeItem?.reportListItemKey === item.reportListItemKey,
    [activeItem],
  )

  const columns: ColumnDef<ReportListItem>[] = useMemo(
    () => [
      {
        id: "select",
        header: () => (
          <SelectAllHeader
            selectableItemCount={selectableItems.length}
            selectedSelectableCount={selectedSelectableCount}
            onSelectAll={handleSelectAll}
          />
        ),
        cell: ({ row }) => (
          <SelectCell
            item={row.original}
            selected={isSelected(row.original)}
            disabled={isItemSelectionDisabled(row.original)}
            onSelectItem={handleSelectItem}
          />
        ),
        size: 40,
      },
      {
        accessorKey: "displayName",
        header: t("grid.columns.report"),
        cell: ({ row }) => (
          <DisplayNameCell
            item={row.original}
            isEditing={editingItemKey === row.original.reportListItemKey}
            editingValue={editingValue}
            onEditValueChange={handleEditValueChange}
            onEditBlur={handleEditBlur(row.original)}
            onEditKeyDown={handleEditKeyDown(row.original)}
            onDoubleClick={handleDoubleClick(row.original)}
            t={t}
          />
        ),
      },
      {
        id: "parameterSummary",
        header: t("grid.columns.parameterSummary"),
        cell: ({ row }) => (
          <ParameterSummaryBadges
            parameters={row.original.report?.parameters}
            isLoading={isParameterSummaryLoading}
          />
        ),
      },
      {
        id: "actions",
        cell: ({ row }) => (
          <ActionCell
            item={row.original}
            onMenuItemClick={handleContextMenuAction}
            menuItems={
              isSelected(row.original)
                ? selectedReportListItemMenuItems
                : reportListItemMenuItems
            }
            selectedCount={isSelected(row.original) ? selectedItems.length : 0}
            t={t}
          />
        ),
        size: 54,
      },
    ],
    [
      items,
      isParameterSummaryLoading,
      selectedItems,
      editingItemKey,
      editingValue,
      handleContextMenuAction,
      handleEditValueChange,
      handleEditBlur,
      handleEditKeyDown,
      handleDoubleClick,
      handleSelectAll,
      handleSelectItem,
      isItemSelectionDisabled,
      isSelected,
      reportListItemMenuItems,
      selectedReportListItemMenuItems,
      selectableItems.length,
      selectedSelectableCount,
      t,
    ],
  )

  const table = useReactTable({
    data: items,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  if (items.length === 0) {
    return (
      <button
        type="button"
        className="flex h-full w-full items-center justify-center bg-transparent border-none cursor-default"
        onClick={() => onActiveItemChange(null)}
      >
        <div className="text-sm text-muted-foreground">
          {t("grid.noReports")}
        </div>
      </button>
    )
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "F2" && selectedItems.length === 1) {
      e.preventDefault()
      handleStartEdit(selectedItems[0])
    }
  }

  const handleGridClick = (e: React.MouseEvent) => {
    // If click target is within the table body rows, let row click handle it.
    // Otherwise (header, empty space below rows), clear active item.
    const target = e.target as HTMLElement
    if (target.closest('[data-slot="context-menu-content"]')) {
      return
    }
    if (!target.closest("tbody tr")) {
      onActiveItemChange(null)
    }
  }

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div className="h-full overflow-auto" onKeyDown={handleKeyDown} onClick={handleGridClick}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                <TableHead className="w-8" />
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            <SortableContext
              items={items.map((item) => item.reportListItemKey)}
              strategy={verticalListSortingStrategy}
            >
              {table.getRowModel().rows.map((row) => {
                const isRowChecked = isSelected(row.original)
                const menuItemsForRow = isRowChecked
                  ? selectedReportListItemMenuItems
                  : reportListItemMenuItems

                return (
                  <ContextMenu key={row.id}>
                    <ContextMenuTrigger asChild>
                      <SortableRow
                        item={row.original}
                        isActive={isActiveItem(row.original)}
                        onRowClick={handleRowClickActivate}
                        onContextMenuOpen={handleRowContextMenuOpen}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <TableCell key={cell.id}>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </TableCell>
                        ))}
                      </SortableRow>
                    </ContextMenuTrigger>
                    <ContextMenuContent>
                      {isRowChecked && (
                        <>
                          <ContextMenuLabel className="bg-muted rounded-sm text-foreground text-sm font-normal">
                            {t("grid.selectedCount", { count: selectedItems.length })}
                          </ContextMenuLabel>
                          <ContextMenuSeparator />
                        </>
                      )}
                      {menuItemsForRow.map((menuItem) =>
                        menuItem.separator ? (
                          <ContextMenuSeparator key={`sep-${menuItem.command}`} />
                        ) : (
                          <ContextMenuItem
                            key={menuItem.command}
                            className={menuItem.destructive ? "text-destructive" : ""}
                            onClick={handleMenuItemClick(menuItem.command, row.original)}
                          >
                            {menuItem.label}
                          </ContextMenuItem>
                        ),
                      )}
                    </ContextMenuContent>
                  </ContextMenu>
                )
              })}
            </SortableContext>
          </TableBody>
        </Table>
      </DndContext>
    </div>
  )
}
